import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { PortalShell } from '../components/PortalShell.tsx';

interface Article {
  id: string;
  categoryId: string | null;
  categoryName: string | null;
  slug: string;
  title: string;
  bodyHtml: string;
  audience: 'public' | 'internal';
  isPublished: boolean;
  authorAgentName: string | null;
  updatedAt: string;
}

interface Props {
  scope: 'agent' | 'portal';
}

export function KbArticlePage({ scope }: Props): React.ReactElement {
  const { slug = '' } = useParams<{ slug: string }>();
  const { agent } = useAuth();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [error, setError] = useState<string | null>(null);

  const apiBase = scope === 'agent' ? '/api/kb' : '/api/portal/kb';
  const backHref = scope === 'agent' ? '/kb' : '/portal/kb';

  useEffect(() => {
    apiFetch<{ article: Article }>(`${apiBase}/articles/by-slug/${encodeURIComponent(slug)}`)
      .then((r) => setArticle(r.article))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load article.');
      });
  }, [apiBase, slug]);

  async function remove() {
    if (!article) return;
    if (!confirm(`Delete "${article.title}"?`)) return;
    try {
      await apiFetch(`/api/kb/articles/${article.id}`, { method: 'DELETE' });
      navigate('/kb');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Delete failed.');
    }
  }

  if (error) {
    const errBody = <p className="text-sm text-[var(--color-danger)]">{error}</p>;
    return scope === 'portal'
      ? <PortalShell active="kb" containerClassName="mx-auto max-w-3xl px-6 py-10 sm:px-8">{errBody}</PortalShell>
      : errBody;
  }
  if (!article) {
    const loadingBody = <p className="text-[var(--color-muted)]">Loading…</p>;
    return scope === 'portal'
      ? <PortalShell active="kb" containerClassName="mx-auto max-w-3xl px-6 py-10 sm:px-8">{loadingBody}</PortalShell>
      : loadingBody;
  }

  const body = (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to={backHref} className="text-sm text-[var(--color-muted)] hover:underline">
          ← Knowledgebase
        </Link>
        {scope === 'agent' && (
          <div className="mt-1 flex items-center gap-2 text-xs">
            <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 uppercase tracking-wide text-[var(--color-muted)]">
              {article.audience}
            </span>
            <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 uppercase tracking-wide text-[var(--color-muted)]">
              {article.isPublished ? 'published' : 'draft'}
            </span>
            {article.categoryName && (
              <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 uppercase tracking-wide text-[var(--color-muted)]">
                {article.categoryName}
              </span>
            )}
          </div>
        )}
        <h1 className="mt-2 text-3xl font-bold text-[var(--color-header)]">{article.title}</h1>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Updated {new Date(article.updatedAt).toLocaleString()}
          {article.authorAgentName ? ` · by ${article.authorAgentName}` : ''}
        </p>
      </div>

      <Card>
        <div
          className="prose prose-sm max-w-none whitespace-pre-wrap text-[var(--color-body)]"
          dangerouslySetInnerHTML={{ __html: article.bodyHtml }}
        />
      </Card>

      {scope === 'agent' && agent && (
        <div className="flex gap-2">
          <Link to={`/kb/${article.slug}/edit`}>
            <Button variant="outline">Edit</Button>
          </Link>
          {agent.role === 'admin' && (
            <Button variant="outline" onClick={() => void remove()}>
              Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );

  if (scope === 'portal') {
    return <PortalShell active="kb" containerClassName="mx-auto max-w-3xl px-6 py-10 sm:px-8">{body}</PortalShell>;
  }
  return body;
}
